
// Deployment trigger: 2026-04-24
import React, { useState, useEffect } from 'react';
import { Users, Eye, MousePointer2, TrendingUp, Calendar, Mail, Smartphone } from 'lucide-react';
import { 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import { Campaign, Project, Lead } from '../types';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';

interface DashboardProps {
  stats: {
    totalLeads: number;
    totalViews: number;
    activeCampaigns: number;
    avgConversion: number;
  };
  campaigns: Campaign[];
  project?: Project | null;
  onOpenUsageHistory?: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ stats, campaigns, project, onOpenUsageHistory }) => {
  const [chartData, setChartData] = useState<{ name: string; leads: number }[]>([]);

  useEffect(() => {
    const fetchLeads = async () => {
      if (!project?.id) return;
      
      try {
        const q = query(collection(db, 'leads'), where('projectId', '==', project.id));
        const snapshot = await getDocs(q);
        
        const leads = snapshot.docs.map(doc => doc.data() as Lead);
        
        // Group by day for the last 7 days
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const today = new Date();
        const last7Days = Array.from({ length: 7 }).map((_, i) => {
          const d = new Date(today);
          d.setDate(d.getDate() - (6 - i));
          return {
            dateStr: d.toISOString().split('T')[0],
            name: days[d.getDay()],
            leads: 0
          };
        });

        leads.forEach(lead => {
          if (!lead.createdAt) return;
          const leadDate = lead.createdAt.split('T')[0];
          const dayData = last7Days.find(d => d.dateStr === leadDate);
          if (dayData) {
            dayData.leads += 1;
          }
        });

        setChartData(last7Days.map(({ name, leads }) => ({ name, leads })));
      } catch (error) {
        console.error("Failed to fetch leads for chart:", error);
      }
    };

    fetchLeads();
  }, [project?.id]);

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <StatCard 
          label="Total Leads" 
          value={stats.totalLeads.toLocaleString()} 
          icon={<Users className="w-5 h-5" />} 
          color="bg-blue-500"
        />
        <StatCard 
          label="Widget Views" 
          value={stats.totalViews.toLocaleString()} 
          icon={<Eye className="w-5 h-5" />} 
          color="bg-purple-500"
        />
        <StatCard 
          label="Active Games" 
          value={stats.activeCampaigns.toString()} 
          icon={<MousePointer2 className="w-5 h-5" />} 
          color="bg-green-500"
        />
        <StatCard 
          label="Avg. Conversion" 
          value={`${stats.avgConversion.toFixed(2)}%`} 
          icon={<TrendingUp className="w-5 h-5" />} 
          color="bg-orange-500"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
        {/* Main Chart */}
        <div className="lg:col-span-2 bg-white p-4 sm:p-6 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-6">
            <h3 className="font-bold text-slate-800 text-lg">Lead Generation Trend</h3>
            <div className="flex items-center gap-2 text-sm text-slate-500 bg-slate-50 px-3 py-1 rounded-lg self-end sm:self-auto">
              <Calendar className="w-4 h-4" />
              <span>Last 7 Days</span>
            </div>
          </div>
          <div className="h-[250px] sm:h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 11}} />
                <YAxis axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 11}} />
                <Tooltip 
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', fontSize: '12px' }}
                />
                <Area type="monotone" dataKey="leads" stroke="#6366f1" strokeWidth={3} fillOpacity={1} fill="url(#colorLeads)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Usage & Quotas */}
        <div className="space-y-6">
          <button 
            onClick={onOpenUsageHistory}
            className="w-full text-left bg-white p-6 rounded-3xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all group cursor-pointer"
          >
             <h3 className="font-bold text-slate-800 text-sm mb-6 flex items-center gap-2 uppercase tracking-widest group-hover:text-indigo-600 transition-colors">
                Monthly Usage
             </h3>
             {project && (
               <div className="space-y-8">
                  <div className="space-y-3">
                     <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2 font-bold text-slate-700">
                           <div className="p-1.5 bg-indigo-50 rounded-lg text-indigo-600"><Mail className="w-3.5 h-3.5" /></div>
                           Emails Sent
                        </div>
                        <span className="font-black text-slate-900">{project.emailUsed || 0} / {project.emailQuota}</span>
                     </div>
                     <div className="w-full bg-slate-100 h-3 rounded-full overflow-hidden border border-slate-200/50">
                        <div 
                          className={`h-full transition-all duration-700 ${ ((project.emailUsed || 0) / project.emailQuota) > 0.9 ? 'bg-red-500' : 'bg-indigo-500' }`} 
                          style={{ width: `${Math.min(100, ((project.emailUsed || 0) / project.emailQuota) * 100)}%` }}
                        />
                     </div>
                  </div>

                  <div className="space-y-3">
                     <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2 font-bold text-slate-700">
                           <div className="p-1.5 bg-emerald-50 rounded-lg text-emerald-600"><Smartphone className="w-3.5 h-3.5" /></div>
                           SMS Sent
                        </div>
                        <span className="font-black text-slate-900">{project.smsUsed || 0} / {project.smsQuota}</span>
                     </div>
                     <div className="w-full bg-slate-100 h-3 rounded-full overflow-hidden border border-slate-200/50">
                        <div 
                          className={`h-full transition-all duration-700 ${ ((project.smsUsed || 0) / project.smsQuota) > 0.9 ? 'bg-red-500' : 'bg-emerald-500' }`} 
                          style={{ width: `${Math.min(100, ((project.smsUsed || 0) / project.smsQuota) * 100)}%` }}
                        />
                     </div>
                  </div>
                  
                  <div className="pt-4 border-t border-slate-100">
                     <p className="text-[10px] text-slate-400 font-medium leading-relaxed italic">
                        Usage metrics reset automatically on the 1st of every month. Click to view full history.
                     </p>
                  </div>
               </div>
             )}
          </button>

          {/* Top Campaigns Mini-list */}
          <div className="bg-white p-4 sm:p-6 rounded-2xl border border-slate-200 shadow-sm">
            <h3 className="font-bold text-slate-800 text-sm mb-6 uppercase tracking-widest">Top Games</h3>
            <div className="space-y-4">
              {campaigns.slice(0, 3).map((c) => (
                <div key={c.id} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 shrink-0">
                    <TrendingUp className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 text-xs truncate">{c.name}</p>
                    <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">{c.type.replace('_', ' ')}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-bold text-slate-800 text-xs">{c.conversionRate}%</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: string; icon: React.ReactNode; color: string }> = ({ label, value, icon, color }) => (
  <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between">
    <div className="flex justify-between items-start mb-3">
      <div className={`p-2.5 rounded-xl text-white ${color}`}>
        {icon}
      </div>
      <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full uppercase">
        Live
      </span>
    </div>
    <div>
      <h3 className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-1">{label}</h3>
      <p className="text-xl font-bold text-slate-800">{value}</p>
    </div>
  </div>
);

export default Dashboard;
